import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  TextInput,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { RootStackParamList, AdminClothingItem } from '../types';
import { itemsService } from '../services/items';
import { adminService } from '../services/admin';
import { moderationService } from '../services/moderation';
import { useDialog } from '../contexts';
import { Colors } from '../constants';

type Props = NativeStackScreenProps<RootStackParamList, 'AdminListings'>;

const STATUS_FILTERS = ['all', 'available', 'reserved', 'sold'] as const;

const STATUS_COLORS: Record<string, string> = {
  available: '#16A34A',
  reserved:  '#D97706',
  sold:      '#6B7280',
};

export const AdminListingsScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();

  const [items, setItems] = useState<AdminClothingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [engagementUnavailable, setEngagementUnavailable] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Admin guard — same pattern as ManageUsersScreen / AdminSettingsScreen
  useEffect(() => {
    const checkAdmin = async () => {
      const isAdmin = await adminService.isCurrentUserAdmin();
      if (!isAdmin) {
        await dialog.alert(
          t('common.errors.unauthorized'),
          t('screens.adminDashboard.notAuthorized'),
          'error'
        );
        navigation.goBack();
      }
    };
    checkAdmin();
  }, []);

  const loadItems = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const result = await itemsService.getAllItemsAdmin({
        status: statusFilter,
        search: debouncedSearch,
        limit: 100,
      });
      let data = result.data;
      // Engagement counts are supplementary — keep showing the list if they fail
      try {
        const engagement = await itemsService.getItemEngagementAdmin(data.map(i => i.id));
        if (engagement === null) {
          // Backend not migrated in this environment yet — show a notice, no counts
          setEngagementUnavailable(true);
        } else {
          setEngagementUnavailable(false);
          data = data.map(i => ({
            ...i,
            conversationCount: engagement[i.id]?.conversationCount ?? 0,
            messageCount: engagement[i.id]?.messageCount ?? 0,
            viewCount: engagement[i.id]?.viewCount ?? 0,
          }));
        }
      } catch (error) {
        // Supplementary data only — degrade to the plain list, never hard-fail
        setEngagementUnavailable(true);
        console.warn('Failed to load engagement counts:', error);
      }
      setItems(data);
      setTotal(result.total);
    } catch (error: any) {
      dialog.alert(t('common.errors.error'), error.message || t('common.errors.loadFailed'), 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter, debouncedSearch]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleDelete = async (item: AdminClothingItem) => {
    const confirmed = await dialog.confirm(
      t('screens.adminListings.deleteTitle'),
      t('screens.adminListings.deleteMessage', { title: item.title }),
      { variant: 'danger' }
    );
    if (!confirmed) return;

    setDeleting(item.id);
    try {
      await moderationService.deleteReportedItem(item.id, 'Removed by admin');
      setItems(prev => prev.filter(i => i.id !== item.id));
      setTotal(prev => prev - 1);
    } catch (error: any) {
      dialog.alert(t('common.errors.error'), error.message || t('common.errors.operationFailed'), 'error');
    } finally {
      setDeleting(null);
    }
  };

  const renderItem = ({ item }: { item: AdminClothingItem }) => {
    const isDeleting = deleting === item.id;

    return (
      <TouchableOpacity style={styles.row} onPress={() => navigation.navigate('ItemDetails', { itemId: item.id })} activeOpacity={0.7}>
        <View style={styles.rowMain}>
          <View style={styles.rowInfo}>
            <Text style={styles.itemTitle} numberOfLines={1}>{item.title}</Text>
            <Text style={styles.itemMeta}>
              {item.seller?.first_name} {item.seller?.last_name}
              {item.club?.name ? ` · ${item.club.name}` : ''}
              {' · '}€{Number(item.price).toFixed(2)}
            </Text>
            {item.conversationCount !== undefined && (
              <View style={styles.engagementRow}>
                <Ionicons
                  name="eye-outline"
                  size={12}
                  color={(item.viewCount ?? 0) > 0 ? '#666' : '#BBB'}
                />
                <Text style={[styles.engagementText, (item.viewCount ?? 0) === 0 && styles.engagementTextEmpty]}>
                  {t('screens.adminListings.views', { count: item.viewCount ?? 0 })}
                </Text>
                <Ionicons
                  name="chatbubbles-outline"
                  size={12}
                  color={item.conversationCount > 0 ? '#666' : '#BBB'}
                  style={styles.engagementGap}
                />
                <Text style={[styles.engagementText, item.conversationCount === 0 && styles.engagementTextEmpty]}>
                  {item.conversationCount === 0
                    ? t('screens.adminListings.noResponses')
                    : `${t('screens.adminListings.conversations', { count: item.conversationCount })} · ${t('screens.adminListings.messages', { count: item.messageCount ?? 0 })}`}
                </Text>
              </View>
            )}
          </View>
          <View style={[styles.statusBadge, { backgroundColor: STATUS_COLORS[item.status] ?? '#999' }]}>
            <Text style={styles.statusText}>{item.status}</Text>
          </View>
        </View>
        <View style={styles.rowActions}>
          <TouchableOpacity
            style={[styles.deleteButton, isDeleting && styles.deleteButtonDisabled]}
            onPress={(e) => {
              if (Platform.OS === 'web') {
                e.stopPropagation();
              }
              handleDelete(item);
            }}
            disabled={isDeleting}
          >
            {isDeleting
              ? <ActivityIndicator size="small" color="#fff" />
              : <Ionicons name="trash-outline" size={18} color="#fff" />
            }
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={[styles.header, Platform.OS === 'web' && styles.headerWeb]}>
        <View style={styles.headerContent}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('AdminDashboard')}>
            <Ionicons name="arrow-back" size={24} color="#007AFF" />
            <Text style={styles.backText}>{t('common.buttons.back')}</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('screens.adminListings.title')}</Text>
          <Text style={styles.headerSubtitle}>
            {t('screens.adminListings.subtitle', { count: total })}
          </Text>
          {engagementUnavailable && (
            <View style={styles.noticeRow}>
              <Ionicons name="information-circle-outline" size={14} color="#8A6D1D" />
              <Text style={styles.noticeText}>{t('screens.adminListings.engagementUnavailable')}</Text>
            </View>
          )}

          {/* Search */}
          <View style={styles.searchRow}>
            <Ionicons name="search-outline" size={16} color="#999" style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              placeholder={t('screens.adminListings.searchPlaceholder')}
              value={search}
              onChangeText={setSearch}
              placeholderTextColor="#999"
            />
          </View>

          {/* Status filter tabs */}
          <View style={styles.tabs}>
            {STATUS_FILTERS.map(s => (
              <TouchableOpacity
                key={s}
                style={[styles.tab, statusFilter === s && styles.tabActive]}
                onPress={() => setStatusFilter(s)}
              >
                <Text style={[styles.tabText, statusFilter === s && styles.tabTextActive]}>
                  {t(`screens.adminListings.status.${s}`)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>

      {/* List */}
      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={Colors.primary} />
        </View>
      ) : (
        <FlatList
          data={items}
          keyExtractor={i => i.id}
          renderItem={renderItem}
          contentContainerStyle={styles.list}
          refreshing={refreshing}
          onRefresh={() => loadItems(true)}
          ItemSeparatorComponent={() => <View style={styles.separator} />}
          ListEmptyComponent={
            <View style={styles.center}>
              <Text style={styles.emptyText}>{t('screens.adminListings.empty')}</Text>
            </View>
          }
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 },
  header: {
    backgroundColor: '#FFFFFF',
    padding: 16,
    paddingTop: Platform.OS === 'web' ? 16 : 50,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  headerWeb: { alignItems: 'center' },
  headerContent: { width: '100%', maxWidth: 1200 },
  backButton: { flexDirection: 'row', alignItems: 'center', marginBottom: 12, gap: 8 },
  backText: { fontSize: 16, color: '#007AFF' },
  headerTitle: { fontSize: 24, fontWeight: 'bold', color: '#333', marginBottom: 4 },
  headerSubtitle: { fontSize: 14, color: '#666', marginBottom: 12 },
  noticeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#FEF6E0',
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginBottom: 12,
  },
  noticeText: { flex: 1, fontSize: 12, color: '#8A6D1D' },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
    borderRadius: 8,
    paddingHorizontal: 10,
    marginBottom: 12,
  },
  searchIcon: { marginRight: 6 },
  searchInput: { flex: 1, height: 36, fontSize: 14, color: '#333' },
  tabs: { flexDirection: 'row', gap: 8 },
  tab: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: '#F5F5F5',
  },
  tabActive: { backgroundColor: '#007AFF' },
  tabText: { fontSize: 13, fontWeight: '600', color: '#666' },
  tabTextActive: { color: '#FFFFFF' },
  list: { padding: 16, ...(Platform.OS === 'web' && { maxWidth: 1200, width: '100%', alignSelf: 'center' as const }) },
  separator: { height: 8 },
  row: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  rowMain: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10 },
  rowInfo: { flex: 1 },
  itemTitle: { fontSize: 15, fontWeight: '600', color: '#333', marginBottom: 2 },
  itemMeta: { fontSize: 12, color: '#888' },
  engagementRow: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 2 },
  engagementText: { fontSize: 12, color: '#666' },
  engagementTextEmpty: { color: '#BBB' },
  engagementGap: { marginLeft: 6 },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
  statusText: { fontSize: 11, fontWeight: '700', color: '#fff', textTransform: 'uppercase' },
  rowActions: { flexDirection: 'row', gap: 8 },
  deleteButton: {
    width: 36, height: 36, borderRadius: 8,
    backgroundColor: '#EF4444',
    justifyContent: 'center', alignItems: 'center',
  },
  deleteButtonDisabled: { opacity: 0.5 },
  emptyText: { fontSize: 16, color: '#888', textAlign: 'center' },
});
